import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { getReadableDomain, isHtml5Creative, getDspFromImpression } from '../../../../../platform/ux/Advertiser/details/components/utils';
import { colors } from '../../../../utils/constants/listOfColors';

const AdvertiserDetailsSignupsController = (props) => {
  const { signedURL, advertiser, setReport, setCreatives, creatives } = props;
  const [reportDataSummary, setReportData] = useState({
    byDates: [],
    signups: [],
    summary: {
      signups: 0,
    },
  });
  const [reportDataRawSummary, setReportRawData] = useState();
  const [reportDataSummaryLoading, setReportLoading] = useState(true);
  const [topCreatives, setTopCreatives] = useState([]);
  const [topImpressions, setTopImpressions] = useState([]);
  const [topInventoryTypes, setTopInventoryTypes] = useState([]);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [sessionDates] = useState(
    JSON.parse(sessionStorage.getItem('advertiserSignupsDate.' + advertiser.id))
  );

  useEffect(() => {
    if (signedURL) {
      const s3UrlSummary = new URL(signedURL.getSignedURL);
      fetch(s3UrlSummary).then(async (response) => {
        if (response && response.ok) {
          try {
            await response.json().then((json) => {
              if (json) {
                let report = [];
                if (json.byDates) {
                  report.push({
                    name: 'Signups - By Dates',
                    value: json.byDates,
                  });
                }

                if (json.signups) {
                  report.push({
                    name: 'Signups - Signups List',
                    value: json.signups.map((s) => ({
                      'Sign Up ID': s.signupId,
                      'Signed Up On': s.signups_tstamp,
                      'Insertion Order': s.insertionOrder,
                      'Attribution Method': Object.keys(s.attributionMethod)[0]
                        .toLowerCase()
                        .replace(/_/g, ' ') // Replace underscores with spaces
                        .replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize first letter of each word,
                      'Complete First Impression': JSON.stringify(s.completeFirstImpression),
                      'Impressions Page URL': s.completeFirstImpression.IMPRESSION_PAGE_URL,
                      'First Creative': s.completeFirstCreative,
                      'Last Impression': s.completeFirstImpression.IMPRESSION_TIME,
                      Impressions: JSON.stringify(s.impressions),
                      'Total Impressions': s.totalImpressions,
                    })),
                  });
                }
                setReport(report);
              }

              let creativesRaw = [];
              if (json.signups) {
                json.signups = json.signups.map((s) => {
                  return {
                    ...s,
                    impressions: s.impressions.map((i) => {
                      const maskCreative = creatives.find((c) => c.creativeID === i.CREATIVEID);

                      const creativeExists = creatives.findIndex((c) => c.name === i.CREATIVEID);
                      if (creativeExists === -1) {
                        creativesRaw.push({
                          name: i.CREATIVEID,
                        });
                      }

                      return {
                        ...i,
                        CREATIVEURL: maskCreative ? maskCreative.url : (i.CREATIVEURL || ''),
                        CREATIVEID: maskCreative ? maskCreative.name : i.CREATIVEID,
                      };
                    }),
                  };
                });
              }

              const { sortedCreatives, sortedImpressionDomains, sortedInventoryTypes } =
                topChartData(json.signups);

              setTopCreatives(sortedCreatives);
              setTopImpressions(sortedImpressionDomains);
              setTopInventoryTypes(sortedInventoryTypes);

              setCreatives(creativesRaw);
              setReportData(json);
              setReportRawData(json);
              setReportLoading(false);
            });
          } catch (err) {
            console.log(err);
            setReportLoading(false);
          }
        } else {
          setReportLoading(false);
        }
      });
    } else {
      // Handle case when there's no signedURL (no signups data)
      setReport([]);
      setReportLoading(false);
    }
  }, [creatives, setCreatives, setReport, signedURL]);

  useEffect(() => {
    if (sessionDates && reportDataRawSummary) {
      changeDatePicker([
        moment(sessionDates[0], 'YYYY-MM-DD'),
        moment(sessionDates[1], 'YYYY-MM-DD'),
      ]);
      setHasSavedSession(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionDates, reportDataRawSummary]);

  const resetSavedSession = () => {
    sessionStorage.removeItem('advertiserSignupsDate.' + advertiser.id);
    if (reportDataRawSummary) {
      setReportData(reportDataRawSummary);
    }
    setHasSavedSession(false);
  };

  const changeDatePicker = (dates) => {
    const datePickerStartDate = dates[0].format('YYYY-MM-DD');
    const datePickerEndDate = dates[1].format('YYYY-MM-DD');

    sessionStorage.setItem(
      'advertiserSignupsDate.' + advertiser.id,
      JSON.stringify([datePickerStartDate, datePickerEndDate])
    );
    setHasSavedSession(true);

    if (!reportDataRawSummary) {
      return;
    }

    const { byDates, signups } = reportDataRawSummary;

    const filteredByDates = byDates.filter(
      (date) =>
        moment(date.date).isSameOrBefore(datePickerEndDate) &&
        moment(date.date).isSameOrAfter(datePickerStartDate)
    );

    const filteredSignups = signups.filter(
      (su) =>
        moment(su.signups_tstamp).isSameOrAfter(datePickerStartDate) &&
        moment(su.signups_tstamp).isSameOrBefore(datePickerEndDate)
    );

    let summary = {
      signups: 0,
      impressions: 0,
      totalImpressions: 0,
    };

    filteredSignups.forEach((su) => {
      summary.impressions += parseFloat(su.totalImpressions);
      summary.signups += 1;
    });

    summary.totalImpressions = filteredByDates.reduce(
      (acc, curr) => (acc += curr.totalImpressions),
      0
    );

    let report = [];
    if (filteredByDates) {
      report.push({
        name: 'Signups - By Dates',
        value: filteredByDates,
      });
    }

    if (filteredSignups) {
      report.push({
        name: 'Signups - Signups List',
        value: filteredSignups.map((s) => ({
          'Sign Up ID': s.signupId,
          'Signed Up On': s.signups_tstamp,
          'Insertion Order': s.insertionOrder,
          'Attribution Method': Object.keys(s.attributionMethod)[0]
            .toLowerCase()
            .replace(/_/g, ' ') // Replace underscores with spaces
            .replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize first letter of each word,
          'Complete First Impression': JSON.stringify(s.completeFirstImpression),
          'Impressions Page URL': s.completeFirstImpression.IMPRESSION_PAGE_URL,
          'First Creative': s.completeFirstCreative,
          'Last Impression': s.completeFirstImpression.IMPRESSION_TIME,
          Impressions: JSON.stringify(s.impressions),
          'Total Impressions': s.totalImpressions,
        })),
      });
      setReport(report);
    }

    const { sortedCreatives, sortedImpressionDomains, sortedInventoryTypes } =
      topChartData(filteredSignups);

    setTopCreatives(sortedCreatives);
    setTopImpressions(sortedImpressionDomains);
    setTopInventoryTypes(sortedInventoryTypes);

    setReportData({
      byDates: filteredByDates,
      signups: filteredSignups,
      summary: {
        ...summary,
      },
    });
  };

  const topChartData = (signups) => {
    const creatives = [];
    const impressionDomains = [];
    const inventoryTypes = [];
    let index = 0;

    (signups || []).forEach((signup) => {
      (signup.impressions || []).forEach((impression) => {
        index++;

        const { impressionDomain, inventoryType } = getReadableDomain(impression, null);
        const dsp = getDspFromImpression(impression);
        const isHtml5 = isHtml5Creative(impression.IMPRESSION_PAGE_URL);

        const creativeExists = creatives.findIndex((c) => c.name === impression.CREATIVEID);
        if (creativeExists === -1) {
          creatives.push({
            name: impression.CREATIVEID,
            impressions: 1,
            signups: 1,
            signupsList: [signup.signupId],
            color: colors[index],
            url: impression.CREATIVEURL,
            isHtml5: isHtml5 || false,
            dsp: dsp || 'Unknown',
          });
        } else {
          const signupExists = creatives[creativeExists].signupsList.findIndex(
            (s) => s === signup.signupId
          );
          if (signupExists === -1) {
            creatives[creativeExists].signupsList.push(signup.signupId);
            creatives[creativeExists].signups += 1;
          }
          creatives[creativeExists].impressions += 1;
          if (isHtml5) {
            creatives[creativeExists].isHtml5 = true;
          }
          if (dsp !== 'Unknown' && (!creatives[creativeExists].dsp || creatives[creativeExists].dsp === 'Unknown')) {
            creatives[creativeExists].dsp = dsp;
          }
        }

        const impressionExists = impressionDomains.findIndex((i) => i.name === impressionDomain);
        if (impressionExists === -1) {
          impressionDomains.push({
            name: impressionDomain,
            unformatted: impressionDomain,
            signups: 1,
            type: inventoryType,
            signupsList: [signup.signupId],
            url: null,
            color: colors[index],
          });
        } else {
          const signupExists = impressionDomains[impressionExists].signupsList.findIndex(
            (s) => s === signup.signupId
          );
          if (signupExists === -1) {
            impressionDomains[impressionExists].signupsList.push(signup.signupId);
            impressionDomains[impressionExists].signups += 1;
          }
        }

        const inventoryTypeExists = inventoryTypes.findIndex((i) => i.type === inventoryType);
        if (inventoryTypeExists === -1) {
          inventoryTypes.push({
            type: inventoryType,
            impressions: 1,
            signups: 1,
            signupsList: [signup.signupId],
            color: colors[index],
          });
        } else {
          const signupExists = inventoryTypes[inventoryTypeExists].signupsList.findIndex(
            (s) => s === signup.signupId
          );
          if (signupExists === -1) {
            inventoryTypes[inventoryTypeExists].signupsList.push(signup.signupId);
            inventoryTypes[inventoryTypeExists].signups += 1;
          }
          inventoryTypes[inventoryTypeExists].impressions += 1;
        }
      });
    });

    const sortedCreatives = creatives.sort((a, b) => b.signups - a.signups);
    const sortedImpressionDomains = impressionDomains.sort((a, b) => b.signups - a.signups);
    const sortedInventoryTypes = inventoryTypes.sort((a, b) => b.signups - a.signups);

    return {
      sortedCreatives,
      sortedImpressionDomains,
      sortedInventoryTypes,
    };
  };

  return React.cloneElement(props.children, {
    ...props.children.props,
    reportDataSummary,
    advertiser,
    reportDataSummaryLoading,
    changeDatePicker,
    resetSavedSession,
    hasSavedSession,
    topCreatives,
    topImpressions,
    topInventoryTypes,
  });
};

export default AdvertiserDetailsSignupsController;
